import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { OrderDto } from '../../dto';
import { createOrder, fetchOrderSettings, updateOrder } from '../../api/ordersApi';
import { saveOrderProducts, type OrderSaveConfirmation } from '../../api/orderProductsApi';
import { fetchCustomerProductPrices } from '../../../customers/api/customerProductPricesApi';
import {
  applyOrderDraftPendingRow,
  buildOrderDraftCreatePayload,
  buildOrderDraftHeaderUpdate,
  buildOrderDraftProductsPayload,
  createEmptyOrderDraft,
  getOrderDraftSaveProblem,
  haveOrderDraftLinesChanged,
  isOrderDraftDirty,
  needsDeliveryModeUpdate,
  orderDraftFromOrder,
  orderDraftLineSignature,
  type OrderDraftHeader,
  type OrderDraftHeaderEditRights,
  type OrderDraftLine,
  type OrderDraftPendingRow,
  type OrderDraftState,
} from './orderDraftModel';

export interface OrderDraftEditorCaps extends OrderDraftHeaderEditRights {
  /** Customer price list fills the price of an added product. */
  canViewSalePrices: boolean;
  /** Sale prices are sent with the lines (otherwise the backend keeps them). */
  canEditSalePrices: boolean;
  /** May set „Dostawa” (our delivery / customer pickup) — the follow-up update of a new order. */
  deliveryMode: boolean;
}

export type OrderDraftSaveResult =
  | { status: 'invalid'; problemKey: string }
  | { status: 'cancelled' }
  | { status: 'unchanged' }
  /** `deliveryModeFailed`: the order exists, only the follow-up „odbiór własny” update failed. */
  | { status: 'created'; order: OrderDto; deliveryModeFailed?: boolean }
  | { status: 'saved' };

// Everything the row saves except its key (the unit conversion „Przelicznik” included).
const pendingRowSignature = (row: OrderDraftPendingRow | null) => (row
  ? `${row.incomplete}|${row.line ? orderDraftLineSignature(row.line) : ''}`
  : '');

export type CustomerPriceMap = Map<string, { fixedPrice: number; currency: string }>;

/**
 * State and commands of the Szkic step (one owner for the new card and the
 * saved draft). A saved order re-reads into the editor whenever it changes,
 * unless the operator has unsaved edits — those are never overwritten.
 */
export function useOrderDraftEditor({
  order,
  isNew = false,
  loadCustomerPrices = true,
  caps,
  confirmation,
  onEdit,
}: {
  /** The saved order; null while it loads, or for a new order (`isNew`). */
  order: OrderDto | null;
  /** A new order, created by the first „Zapisz szkic” / „Stwórz ofertę”. */
  isNew?: boolean;
  /** Read the customer's price list (only needed while products are being added). */
  loadCustomerPrices?: boolean;
  caps: OrderDraftEditorCaps;
  confirmation: OrderSaveConfirmation;
  /** The operator changed what a save problem is about (customer, lines, manual number, the „Dodaj produkt” row). */
  onEdit?: () => void;
}) {
  const [saved, setSaved] = useState<OrderDraftState>(() => (order ? orderDraftFromOrder(order) : createEmptyOrderDraft()));
  const [draft, setDraft] = useState<OrderDraftState>(saved);
  const [saving, setSaving] = useState(false);
  const [invalidSave, setInvalidSave] = useState<{ problemKey: string; seq: number } | null>(null);
  const [customerPrices, setCustomerPrices] = useState<CustomerPriceMap>(new Map());
  // The „Dodaj produkt” row, reported by the editor: saving adds a complete one (a ref for the save, state for the hint).
  const [pendingRow, setPendingRowState] = useState<OrderDraftPendingRow | null>(null);
  const pendingRowRef = useRef<OrderDraftPendingRow | null>(null);
  const setPendingRow = useCallback((next: OrderDraftPendingRow | null) => {
    // Same row content (e.g. only the price list was re-read): keep the current object.
    if (pendingRowSignature(pendingRowRef.current) === pendingRowSignature(next)) return;
    pendingRowRef.current = next;
    setPendingRowState(next);
  }, []);
  const onEditRef = useRef(onEdit);
  onEditRef.current = onEdit;
  const dirty = useMemo(() => isOrderDraftDirty(saved, draft), [draft, saved]);
  // Only the lines move the margin plan the server computes (header edits do not).
  const linesDirty = useMemo(() => haveOrderDraftLinesChanged(saved.lines, draft.lines), [draft.lines, saved.lines]);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // A saved order (and every refresh after a save) re-seeds a clean editor.
  useEffect(() => {
    if (!order || dirtyRef.current) return;
    const next = orderDraftFromOrder(order);
    setSaved(next);
    setDraft(next);
  }, [order]);

  // New order: the line currency starts at the user's order currency setting.
  useEffect(() => {
    if (!isNew) return undefined;
    let ignore = false;
    fetchOrderSettings()
      .then((settings) => {
        if (ignore || !settings.currency) return;
        const currency = settings.currency;
        setSaved((current) => ({ ...current, header: { ...current.header, currency } }));
        setDraft((current) => ({ ...current, header: { ...current.header, currency } }));
      })
      .catch(() => undefined);
    return () => { ignore = true; };
    // Only once per new card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { customerId, useManualNumbering, manualOrderNumber } = draft.header;
  const draftLines = draft.lines;
  useEffect(() => {
    onEditRef.current?.();
  }, [customerId, useManualNumbering, manualOrderNumber, draftLines, pendingRow]);
  useEffect(() => {
    if (!customerId || !caps.canViewSalePrices || !loadCustomerPrices) {
      setCustomerPrices(new Map());
      return undefined;
    }
    let ignore = false;
    fetchCustomerProductPrices(customerId)
      .then((prices) => {
        if (!ignore) {
          setCustomerPrices(new Map(prices.map((price) => [
            price.productVariantId,
            { fixedPrice: price.fixedPrice, currency: price.currency },
          ])));
        }
      })
      .catch(() => { if (!ignore) setCustomerPrices(new Map()); });
    return () => { ignore = true; };
  }, [caps.canViewSalePrices, customerId, loadCustomerPrices]);

  const setHeader = useCallback((patch: Partial<OrderDraftHeader>) => {
    setDraft((current) => ({ ...current, header: { ...current.header, ...patch } }));
  }, []);
  const addLine = useCallback((line: OrderDraftLine) => {
    setDraft((current) => ({ ...current, lines: [...current.lines, line] }));
  }, []);
  const updateLine = useCallback((key: string, update: (line: OrderDraftLine) => OrderDraftLine) => {
    setDraft((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? update(line) : line)),
    }));
  }, []);
  const removeLine = useCallback((key: string) => {
    setDraft((current) => ({ ...current, lines: current.lines.filter((line) => line.key !== key) }));
  }, []);
  const discard = useCallback(() => setDraft(saved), [saved]);

  /**
   * `patch` changes the draft in the same step as the save (the „Ceny sprzedaży”
   * window puts its prices into the lines): a state update made just before
   * `save()` would not be seen by it. The patched draft stays on the card even if
   * the save is refused or cancelled, so nothing typed is lost.
   */
  const save = useCallback(async (patch?: (current: OrderDraftState) => OrderDraftState): Promise<OrderDraftSaveResult> => {
    const pending = pendingRowRef.current;
    const base = patch ? patch(draft) : draft;
    if (base !== draft) setDraft(base);
    // A complete row left in „Dodaj produkt” is added to the draft, as „Dodaj produkt” would.
    const toSave = applyOrderDraftPendingRow(base, pending);
    const problemKey = getOrderDraftSaveProblem(toSave, pending, saved.lines);
    if (problemKey) {
      // „Więcej danych” opens the section the problem is about (`seq` makes a repeated problem fire again).
      setInvalidSave((current) => ({ problemKey, seq: (current?.seq ?? 0) + 1 }));
      return { status: 'invalid', problemKey };
    }
    setSaving(true);
    try {
      const lineOptions = { includeSalePrices: caps.canEditSalePrices, fallbackCurrency: toSave.header.currency };
      if (isNew) {
        const created = await createOrder(buildOrderDraftCreatePayload(toSave, {
          includeSalePrices: caps.canEditSalePrices,
          editAccountManager: caps.accountManager,
          viewCustomerPayments: caps.customerPayments,
        }));
        // „Odbiór własny” is not part of the create contract: the update right after sets it.
        // The order already exists, so a failure here must not look like a failed create
        // (a retry would create a duplicate): the caller still opens the card and warns.
        let deliveryModeFailed = false;
        if (needsDeliveryModeUpdate(toSave) && created.id && caps.deliveryMode) {
          try {
            await updateOrder(created.id, { deliveryMode: toSave.header.deliveryMode });
          } catch {
            deliveryModeFailed = true;
          }
        }
        setSaved(toSave);
        if (toSave !== base) setDraft(toSave);
        return { status: 'created', order: created, ...(deliveryModeFailed ? { deliveryModeFailed } : {}) };
      }
      if (!order) return { status: 'unchanged' };
      const headerUpdate = buildOrderDraftHeaderUpdate(saved.header, toSave.header, caps);
      const linesChanged = haveOrderDraftLinesChanged(saved.lines, toSave.lines);
      if (Object.keys(headerUpdate).length === 0 && !linesChanged) return { status: 'unchanged' };
      if (Object.keys(headerUpdate).length > 0) {
        if (!await updateOrder(order.id, headerUpdate, confirmation)) return { status: 'cancelled' };
      }
      if (linesChanged) {
        if (!await saveOrderProducts(order.id, buildOrderDraftProductsPayload(toSave.lines, lineOptions), undefined, confirmation)) {
          return { status: 'cancelled' };
        }
      }
      // The refreshed order re-seeds the editor (saved line ids, server values).
      dirtyRef.current = false;
      setSaved(toSave);
      if (toSave !== base) setDraft(toSave);
      return { status: 'saved' };
    } finally {
      setSaving(false);
    }
  }, [caps, confirmation, draft, isNew, order, saved]);

  return {
    draft,
    /** The lines as last saved (the server prices these). */
    savedLines: saved.lines,
    dirty,
    linesDirty,
    saving,
    /** The last refused save (why, and a counter): lets „Więcej danych” open the section with the problem. */
    invalidSave,
    customerPrices,
    setHeader,
    addLine,
    updateLine,
    removeLine,
    discard,
    pendingRow,
    setPendingRow,
    save,
  };
}

export type OrderDraftEditorController = ReturnType<typeof useOrderDraftEditor>;
